import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { CustomerService } from 'src/app/core/services/customers.service';

@Component({
  selector: 'app-contact-quick-add-popup',
  templateUrl: './contact-quick-add-popup.component.html'
})
export class ContactQuickAddPopupComponent implements OnInit {
  @Input() customerId: number | null = null;

  formData: FormGroup;
  submitted = false;
  isSaving = false;
  customerDetail: any = null;
  readonly contactTypeList = [
    { id: 'OWNER', label: 'Owner' },
    { id: 'AUTHORIZED_PERSON', label: 'Authorised Person' }
  ];

  constructor(
    private formBuilder: FormBuilder,
    private customerService: CustomerService,
    public activeModal: NgbActiveModal
  ) {
    this.formData = this.formBuilder.group({
      contactType: ['OWNER', [Validators.required]],
      firstName: [null],
      lastName: [null],
      contactNumber: [null, [Validators.required, Validators.pattern('^[0-9]{10}$')]],
      emailId: [null, [Validators.pattern('[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,}$')]],
      aadharNo: [null, [Validators.pattern('^[0-9]{12}$')]],
      panCardNo: [null, [Validators.pattern('^[A-Za-z0-9]{10}$')]]
    }, { updateOn: 'blur' });
  }

  ngOnInit() {
    if (!this.customerId) {
      Swal.fire('Customer not found', '', 'warning');
      this.activeModal.dismiss('missing-customer');
      return;
    }

    this.customerService.getById(this.customerId).subscribe(
      (res: any) => {
        if (res?.isError) {
          Swal.fire('Unable to load customer details', '', 'error');
          this.activeModal.dismiss('load-failed');
          return;
        }
        this.customerDetail = res?.data || null;
      },
      () => {
        Swal.fire('Unable to load customer details', '', 'error');
        this.activeModal.dismiss('load-failed');
      }
    );
  }

  onSubmit() {
    this.submitted = true;
    if (this.formData.invalid || !this.customerDetail?.id) {
      this.formData.markAllAsTouched();
      return;
    }

    const newContact = {
      ...this.formData.value,
      contactType: this.resolveContactTypeLabel(this.formData.get('contactType')?.value)
    };

    const payload = {
      id: this.customerDetail?.id,
      name: this.customerDetail?.name,
      gstNo: this.customerDetail?.gstNo ?? null,
      panNo: this.customerDetail?.panNo ?? null,
      tanNo: this.customerDetail?.tanNo ?? null,
      customerType: this.customerDetail?.customerType ?? null,
      contacts: [...(this.customerDetail?.contacts || []), newContact],
      addresses: this.customerDetail?.addresses || [],
      isUser: this.toBoolean(this.customerDetail?.isUser, !!this.customerDetail?.user),
      user: this.customerDetail?.user || null
    };

    this.isSaving = true;
    this.customerService.saveCustomerData(payload)
      .pipe(finalize(() => { this.isSaving = false; }))
      .subscribe(
        (res: any) => {
          if (res?.isError) {
            Swal.fire(res?.error || 'Unable to save contact', '', 'error');
            return;
          }
          const savedId = Number(res?.data?.id || this.customerDetail?.id);
          if (Number.isFinite(savedId) && savedId > 0) {
            this.customerService.getById(savedId).subscribe(
              (detailRes: any) => {
                const savedCustomer = detailRes?.data || res?.data || null;
                const savedContact = this.findSavedContact(savedCustomer, newContact);
                this.activeModal.close({ customer: savedCustomer, contact: savedContact });
              },
              () => this.activeModal.close({ customer: res?.data || this.customerDetail, contact: newContact })
            );
            return;
          }
          this.activeModal.close({ customer: res?.data || this.customerDetail, contact: newContact });
        },
        () => Swal.fire('Unable to save contact', '', 'error')
      );
  }

  private findSavedContact(customer: any, newContact: any): any {
    const contacts = customer?.contacts || [];
    return contacts.find((contact: any) =>
      contact?.contactNumber === newContact?.contactNumber &&
      (contact?.emailId || null) === (newContact?.emailId || null)
    ) || contacts.find((contact: any) =>
      contact?.contactNumber === newContact?.contactNumber
    ) || newContact;
  }

  private resolveContactTypeLabel(value: any): string {
    const match = this.contactTypeList.find((item) => item.id === value);
    return match?.label || 'Owner';
  }

  private toBoolean(value: any, fallback = false): boolean {
    if (value === true || value === false) {
      return value;
    }
    if (value === 1 || value === '1' || value === 'true') {
      return true;
    }
    if (value === 0 || value === '0' || value === 'false') {
      return false;
    }
    return fallback;
  }
}
